import { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';

import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import {
  ALL_TIME_RANGE_PRESET,
  TimeRangeValue,
  resolveDateRangeParam,
} from '#components/time-range.utils';
import DropdownFilterBox from '#components/DropdownFilterBox';
import RecencyDateText from '#components/RecencyDateText';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  useGetUsersQuery,
  useDeleteUserMutation,
} from '#services/user';
import URLSearchParamsEx from '#utils/url-ex';
import { getCanEdit, getCanDelete } from '#common/feature';
import { USER_ROLES, USER_ROLES_TYPES } from '#common/constants';
import {
  formatCurrency,
  formatDate,
} from '#common/utils';
import {
  setDateRange,
  setFilter,
  setFilterKey,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
} from './list.slice';

interface AuthUser {
  id?: string | number;
  isAdmin?: boolean;
  isSalesperson?: boolean;
  isCustomer?: boolean;
  isStaff?: boolean;
}

interface UserRow {
  id?: string | number;
  username?: string;
  role?: string;
  totalSpentAmount?: number;
  orderCount?: number;
  createdDate?: string;
  lastLoginAt?: string;
  inactivated?: boolean;
  [key: string]: unknown;
}

interface UserListSlice {
  dateRange: TimeRangeValue;
  filter: string;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  rows: EnhancedTableRow[];
  rowCount: number;
  pageSize: number;
}

export default function UserList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteUser] = useDeleteUserMutation();

  const currentUser = useSelector((state: { auth: { userInfo: AuthUser } }) => state.auth.userInfo);
  const filterKey = useSelector((state: { userList: UserListSlice }) => state.userList.filterKey);
  const filter = useSelector((state: { userList: UserListSlice }) => state.userList.filter);
  const dateRange = useSelector((state: { userList: UserListSlice }) => state.userList.dateRange);
  const headerFilters = useSelector((state: { userList: UserListSlice }) => state.userList.headerFilters);
  const order = useSelector((state: { userList: UserListSlice }) => state.userList.order);
  const orderBy = useSelector((state: { userList: UserListSlice }) => state.userList.orderBy);
  const pageIndex = useSelector((state: { userList: UserListSlice }) => state.userList.pageIndex);
  const rows = useSelector((state: { userList: UserListSlice }) => state.userList.rows);
  const rowCount = useSelector((state: { userList: UserListSlice }) => state.userList.rowCount);
  const pageSize = useSelector((state: { userList: UserListSlice }) => state.userList.pageSize);
  const [debouncedPageIndex] = useDebounce(pageIndex, 100);
  const [filterForQuery, setFilterForQuery] = useState(filter);
  const [isInitialMount, setIsInitialMount] = useState(true);

  const {
    showBackdrop,
    hideBackdrop,

    showDialog,
    hideDialog,
    setDialogData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const dateRangeParam = resolveDateRangeParam('createdDate', dateRange);

  const query = useGetUsersQuery({
    dateRange: dateRangeParam,
    order,
    orderBy,
    filter: filterForQuery,
    filterKey,
    headerFilters,
    pageIndex: debouncedPageIndex,
    pageSize,
  }, {
    refetchOnMountOrArgChange: true,
  });

  // Restore state from URL parameters on component mount only
  useEffect(() => {
    if (!isInitialMount) return;

    const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    if (urlParams.values.filter !== undefined) {
      dispatch(setFilter(urlParams.values.filter as string));
      setFilterForQuery(urlParams.values.filter as string);
    }
    if (urlParams.values.filterKey !== undefined) {
      dispatch(setFilterKey(urlParams.values.filterKey as string));
    }
    if (urlParams.values.headerFilters !== undefined) {
      dispatch(setHeaderFilters(urlParams.values.headerFilters as Record<string, Record<string, boolean>>));
    }
    if (urlParams.values.order !== undefined) {
      dispatch(setOrder(urlParams.values.order as string));
    }
    if (urlParams.values.orderBy !== undefined) {
      dispatch(setOrderBy(urlParams.values.orderBy as string));
    }
    if (urlParams.values.pageSize !== undefined) {
      dispatch(setPageSize(urlParams.values.pageSize as number));
    }

    setIsInitialMount(false);
  }, [location.search, isInitialMount, dispatch]);

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'username',
    label: 'Store Name',
    renderer: (row, idx) => {
      const user = row as unknown as UserRow;
      const canEdit = getCanEdit(
        currentUser,
        row,
      );

      if (!canEdit) {
        return user.username;
      }

      const userIndex = pageIndex * pageSize + idx + 1;
      const qs = new URLSearchParamsEx({
        order,
        orderBy,
        filter,
        filterKey,
        headerFilters,
        pageSize,
        userIndex,
        userId: user.id,
        // The detail page re-runs the list query to resolve the row index (and
        // to walk prev/next), so it needs the same date window the list used —
        // without it the index points into the unfiltered list.
        ...(dateRangeParam ? { dateRange: dateRangeParam } : {}),
      }).toString();
      const url = `/management/user/detail?${qs}`;
      return (
        <Link
          href={url}
          onClick={(e) => {
            e.preventDefault();
            void navigate(url);
          }}
        >
          {user.username}
        </Link>
      );
    },
  }, {
    id: 'code',
    label: 'Account Code',
  }, {
    id: 'name',
    label: 'Store Owner',
  }, {
    id: 'address',
    label: 'Location',
  }, {
    id: 'totalSpentAmount',
    label: 'Total Spent',
    renderer: (row) => {
      const user = row as unknown as UserRow;
      if ((currentUser.isAdmin || currentUser.isSalesperson) && user.role === USER_ROLES.CUSTOMER) {
        return Number.isNaN(Number(user.totalSpentAmount)) ? '-' : formatCurrency(user.totalSpentAmount as number);
      }
      return '-';
    },
  }, {
    id: 'orderCount',
    label: 'Orders',
    renderer: (row) => {
      const user = row as unknown as UserRow;
      return user.role === USER_ROLES.CUSTOMER
        ? user.orderCount
        : '-';
    },
  }, {
    id: 'createdDate',
    label: 'Created Date',
    renderer: (row) => formatDate((row as unknown as UserRow).createdDate as string, '-'),
  }, {
    id: 'role',
    label: 'Role',
    filters: USER_ROLES_TYPES,
  }, {
    id: 'inactivated',
    label: 'Status',
    renderer: (row) => ((row as unknown as UserRow).inactivated ? <Chip size="small" label="Inactive" color="warning" /> : <Chip size="small" label="Active" color="success" />),
  }, {
    id: 'action',
    label: 'Action',
    sortable: false,
    renderer: (row) => {
      const user = row as unknown as UserRow;
      const canDelete = getCanDelete(
        currentUser,
        row,
      );
      const canEdit = getCanEdit(
        currentUser,
        row,
      );

      const handleDeleteUser = () => {
        setDialogData({
          title: 'Delete',
          text: 'Are you sure you want to delete this user?',
          disagree: 'Cancel',
          agree: 'Delete',
          onDisagree: () => hideDialog(),
          onAgree: () => {
            hideDialog();
            showBackdrop();

            void deleteUser({
              userId: user.id,
              order,
              orderBy,
              filter,
              pageIndex,
              pageSize,
            }).then((res) => {
              simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `User "${user.username}" was deleted.`);
            }).finally(() => {
              hideBackdrop();
            });
          },
        });
        showDialog();
      };

      const actionButtons = [];
      if (canEdit) {
        actionButtons.push(
          <Link
            key="btn-edit"
            component="button"
            onClick={() => { void navigate(`/management/user/edit?userId=${user.id}`); }}
          >
            Edit
          </Link>,
        );
      }
      if (canDelete) {
        if (actionButtons.length) {
          actionButtons.push(<span key="span">&nbsp;/&nbsp;</span>);
        }
        actionButtons.push(
          <Link
            key="btn-delete"
            component="button"
            disabled={!canDelete}
            onClick={handleDeleteUser}
          >
            Delete
          </Link>,
        );
      }

      return (
        <Box>{actionButtons}</Box>
      );
    },
  }];

  if (currentUser.isAdmin) {
    const createdDateIndex = tableHeaders.findIndex((column) => column.id === 'createdDate');
    const lastLoginColumn = {
      id: 'lastLoginAt',
      label: 'Last Login',
      renderer: (row: EnhancedTableRow) => (
        <RecencyDateText date={(row as unknown as UserRow).lastLoginAt} fallback="-" />
      ),
    };

    if (createdDateIndex >= 0) {
      tableHeaders.splice(createdDateIndex, 0, lastLoginColumn);
    } else {
      tableHeaders.push(lastLoginColumn);
    }
  }

  const tableProps: EnhancedTableProps = {
    name: 'variants',

    headers: tableHeaders,

    enableSort: true,
    order,
    setOrder,
    orderBy,
    setOrderBy,

    headerFilters,
    setHeaderFilters,

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,

    timeRange: {
      value: dateRange,
      onChange: (value) => dispatch(setDateRange(value)),
      columnLabel: 'Created',
      defaultPreset: ALL_TIME_RANGE_PRESET,
      entityLabel: 'customers',
    },

    rows,
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Users
      </Box>

      <Box>
        <DropdownFilterBox
          width={600}
          dropdownItems={[{
            value: 'username',
            text: 'Store Name',
          }, {
            value: 'name',
            text: 'Contact Info (Name/Email/Phone)',
          }, {
            value: 'collectionName',
            text: 'Collection (Purchase)',
          }]}
          filter={filter}
          filterKey={filterKey}
          filterPlaceholder="Filter users"
          dispatch={dispatch as (action: unknown) => void}
          setFilter={setFilter}
          setFilterKey={setFilterKey}
          setFilterForQuery={setFilterForQuery}
        />
      </Box>

      <EnhancedTable
        {...tableProps}
      />
    </>
  );
}
